/**
 * Console Logger Utility
 * 
 * Globally controls console printing based on production mode and verbose flag.
 * 
 * Usage:
 *   import { log, error, warn, info, debug } from './utils/consoleLogger';
 * 
 * Configuration:
 *   - Set ENVIRONMENT to 'production' to disable console in production
 *   - Set verbose=true attribute on root Div to enable verbose mode in production
 */

// Environment flag - set to 'production' to enable production mode
// Can be set via: window.__CONSOLE_ENV__ = 'production'
// Defaults to process.env.NODE_ENV or 'development'
let ENVIRONMENT = process.env.NODE_ENV || 'development';

// Allow runtime override of environment
if (typeof window !== 'undefined' && window.__CONSOLE_ENV__ !== undefined) {
  ENVIRONMENT = window.__CONSOLE_ENV__;
}

const IS_PRODUCTION = ENVIRONMENT === 'production';

// Cache for verbose mode check
let verboseModeCache = null;
let configuredRootDivId = null;

/**
 * Finds the root div element by checking common IDs or the configured rootDivId
 * @returns {HTMLElement|null} The root div element or null
 */
const findRootDiv = () => {
  // First, try the configured rootDivId if set
  if (configuredRootDivId) {
    const element = document.getElementById(configuredRootDivId);
    if (element) return element;
  }

  // Try common root div IDs
  const commonIds = ['root', 'app', 'react-root', 'main'];
  for (const id of commonIds) {
    const element = document.getElementById(id);
    if (element) {
      // Check if it has the verbose attribute
      const verboseAttr = element.getAttribute('verbose');
      if (verboseAttr !== null) {
        configuredRootDivId = id; // Cache the found rootDivId
        return element;
      }
    }
  }

  // If no element with verbose attribute found, use the first common ID that exists
  for (const id of commonIds) {
    const element = document.getElementById(id);
    if (element) {
      configuredRootDivId = id;
      return element;
    }
  }

  return null;
};

/**
 * Checks if verbose mode is enabled by looking for verbose=true in the root Div
 * @returns {boolean} True if verbose mode is enabled
 */
const isVerboseMode = () => {
  // Use cached value if available
  if (verboseModeCache !== null) {
    return verboseModeCache;
  }

  try {
    const rootElement = findRootDiv();
    if (rootElement) {
      const verboseAttr = rootElement.getAttribute('verbose');
      const isVerbose = verboseAttr === 'true' || verboseAttr === '';
      verboseModeCache = isVerbose;
      return isVerbose;
    }
  } catch (error) {
    // If document is not available (e.g., SSR), default to not verbose
    verboseModeCache = false;
    return false;
  }

  verboseModeCache = false;
  return false;
};

/**
 * Determines if console output should be enabled
 * @returns {boolean} True if console output should be enabled
 */
const shouldLog = () => {
  // Always log in development mode
  if (!IS_PRODUCTION) {
    return true;
  }

  // In production, only log if verbose mode is enabled
  return isVerboseMode();
};

/**
 * Creates a wrapped console method that respects production and verbose mode
 * @param {Function} consoleMethod - The original console method (e.g., console.log)
 * @returns {Function} Wrapped console method
 */
const createLogger = (consoleMethod) => {
  return function(...args) {
    if (shouldLog()) {
      consoleMethod.apply(console, args);
    }
  };
};

/**
 * Sets the root div ID for verbose mode checking
 * @param {string} rootDivId - The ID of the root div element
 */
const setRootDivId = (rootDivId) => {
  configuredRootDivId = rootDivId;
  verboseModeCache = null; // Clear cache when rootDivId changes
};

/**
 * Clears the verbose mode cache (useful if rootDiv changes dynamically)
 */
const clearVerboseCache = () => {
  verboseModeCache = null;
  configuredRootDivId = null;
};

// Export wrapped console methods
export const log = createLogger(console.log);
export const error = createLogger(console.error);
export const warn = createLogger(console.warn);
export const info = createLogger(console.info);
export const debug = createLogger(console.debug);
export const trace = createLogger(console.trace);
export const table = createLogger(console.table);
export const group = createLogger(console.group);
export const groupEnd = createLogger(console.groupEnd);
export const groupCollapsed = createLogger(console.groupCollapsed);
export const time = createLogger(console.time);
export const timeEnd = createLogger(console.timeEnd);
export const timeLog = createLogger(console.timeLog);
export const dir = createLogger(console.dir);
export const dirxml = createLogger(console.dirxml);
export const assert = createLogger(console.assert);
export const count = createLogger(console.count);
export const countReset = createLogger(console.countReset);
export const clear = createLogger(console.clear);

// Export utility functions
export { clearVerboseCache, isVerboseMode, shouldLog, setRootDivId };

// Export default object with all methods for convenience
export default {
  log,
  error,
  warn,
  info,
  debug,
  trace,
  table,
  group,
  groupEnd,
  groupCollapsed,
  time,
  timeEnd,
  timeLog,
  dir,
  dirxml,
  assert,
  count,
  countReset,
  clear,
  clearVerboseCache,
  isVerboseMode,
  shouldLog,
  setRootDivId,
};

